import type { FunctionComponent } from 'react';

import { translateString } from 'i18n/translateString';
import { useAppSelector } from 'redux-store/hooks';
import { selectIsSceneLoadingScreenVisible, selectShowAppLoadingScreen } from 'redux-store/rootReducer';
import styled from 'styled-components';
import { Text } from 'ui/Text';
import { COLORS } from 'utils/constants/colors';
import { DataTestIdEnum } from 'utils/constants/elements';
import { Z_INDEXES } from 'utils/constants/zIndex';

import ListView, { ListViewItem } from '@ingka/list-view';
import Loading, { LoadingBall } from '@ingka/loading';
import Skeleton, { type SkeletonProps } from '@ingka/skeleton';

interface IProps {
  $isScene: boolean;
}

const LoadingScreenContainer = styled.div<IProps>`
  position: ${({ $isScene }) => {
    return $isScene ? 'absolute' : 'fixed';
  }};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${({ $isScene }) => {
    return $isScene ? `` : `${Z_INDEXES.LOADING_SCREEN_CONTAINER}`;
  }};
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ $isScene }) => {
    return $isScene ? `${COLORS.BACKGROUND_SCENE}` : `${COLORS.BACKGROUND_WHITE}`;
  }};
  width: ${({ $isScene }) => {
    return $isScene ? '' : '100%';
  }};
  height: ${({ $isScene }) => {
    return $isScene ? '' : '100%';
  }};
`;

const LoadingScreenWrapper: FunctionComponent<IProps> = (props) => {
  const { $isScene } = props;

  return (
    <LoadingScreenContainer $isScene={$isScene} data-testid={DataTestIdEnum.LoadingScreen}>
      <Loading text="">
        <LoadingBall size="large" />
      </Loading>
    </LoadingScreenContainer>
  );
};

export const LoadingScreenScene: FunctionComponent = () => {
  const showSceneLoadingScreen = useAppSelector(selectIsSceneLoadingScreenVisible);

  if (!showSceneLoadingScreen) {
    return null;
  }

  return <LoadingScreenWrapper $isScene={true} />;
};

export const LoadingScreenApp: FunctionComponent = () => {
  const isVisible = useAppSelector(selectShowAppLoadingScreen);

  if (!isVisible) {
    return null;
  }

  return <LoadingScreenWrapper $isScene={false} />;
};

const listSkeletonProps: SkeletonProps = {
  width: '240px',
  height: '18px'
};

export const ShareDesignLoadingSkeleton: FunctionComponent = () => {
  return (
    <>
      <Text textStyle={'Heading.M'} $marginBottom>
        {translateString('shareDesignCardDefaultTitle')}
      </Text>
      <ListView id="shareDesignLoadingSkeleton">
        <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
        <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
        <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
        <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
      </ListView>
    </>
  );
};

export const CustomizeMenuLoadingSkeleton = () => {
  return (
    <ListView id="CustomizeMenuLoadingSkeleton">
      <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
      <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
      <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
      <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
      <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
      <ListViewItem title={<Skeleton {...listSkeletonProps} />} />
    </ListView>
  );
};

const DesignCodeWrapper = styled.div`
  display: flex;
  align-items: center;

  .text {
    margin-inline-end: 0.25rem;
  }
`;

export const CopyDesignLoadingSkeleton: FunctionComponent = () => {
  return (
    <DesignCodeWrapper>
      <Text>{translateString('designCodeTitle')}</Text>
      <Skeleton className="designCode" width="80px" height="18px" />
    </DesignCodeWrapper>
  );
};
